import React, { useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle, AlertTriangle, Droplets,
  CreditCard, QrCode, X,
} from 'lucide-react';
import CustomerMasterLayout from '../../../../layouts/customer/CustomerMasterLayout';
import '../../../../assets/styles/customer/CustomerPanelDashboard.css';

/* ══════════════════════════════════
   QR MODAL
══════════════════════════════════ */
const QRModal = ({ onClose }) => (
  <div className="cdash-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
    <div className="cdash-qr-modal cdash-modal-anim">
      <button className="cdash-qr-modal__x" onClick={onClose}><X size={18} /></button>
      <div className="cdash-qr-modal__text">
        <h3 className="cdash-qr-modal__title">Your QR Code</h3>
      </div>
      <div className="cdash-qr-box">
        <svg width="110" height="110" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="4"  y="4"  width="26" height="26" rx="3" stroke="var(--secondary-color)" strokeWidth="3.5" fill="none"/>
          <rect x="12" y="12" width="10" height="10" rx="1.5" fill="var(--secondary-color)"/>
          <rect x="42" y="4"  width="26" height="26" rx="3" stroke="var(--secondary-color)" strokeWidth="3.5" fill="none"/>
          <rect x="50" y="12" width="10" height="10" rx="1.5" fill="var(--secondary-color)"/>
          <rect x="4"  y="42" width="26" height="26" rx="3" stroke="var(--secondary-color)" strokeWidth="3.5" fill="none"/>
          <rect x="12" y="50" width="10" height="10" rx="1.5" fill="var(--secondary-color)"/>
          <rect x="42" y="42" width="8"  height="8"  rx="1.5" fill="var(--secondary-color)"/>
          <rect x="56" y="42" width="8"  height="8"  rx="1.5" fill="var(--secondary-color)"/>
          <rect x="42" y="56" width="8"  height="8"  rx="1.5" fill="var(--secondary-color)"/>
          <rect x="55" y="53" width="13" height="15" rx="1.5" fill="var(--secondary-color)"/>
        </svg>
      </div>
      <p className="cdash-qr-modal__sub">Show this QR to your distributor when they deliver milk.</p>
      <span className="cdash-qr-modal__badge">Active Customer</span>
    </div>
  </div>
);

/* ══════════════════════════════════
   DAILY REQUIREMENTS CARD
══════════════════════════════════ */
const LITRE_OPTIONS = Array.from(
  { length: 60 },
  (_, i) => parseFloat(((i + 1) * 0.5).toFixed(1))
);

const DailyRequirementsCard = () => {
  const [selectedLtrs, setSelectedLtrs] = useState(2);
  const [saved,        setSaved]        = useState(false);
  const [loading,      setLoading]      = useState(false);

  const handleUpdate = async () => {
    setLoading(true);
    try {
      // await api.patch('/customer/daily-requirement', { litres: selectedLtrs });
      await new Promise(r => setTimeout(r, 600));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      // handle error
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cdash-card cdash-anim cdash-req-card" style={{ animationDelay: '0.34s' }}>
      <div className="cdash-req-card__header">
        <div className="cdash-req-card__icon-wrap">
          <Droplets size={17} strokeWidth={2} />
        </div>
        <h2 className="cdash-card__title cdash-req-card__title">My Daily Requirement</h2>
      </div>

      <div className="cdash-req-card__display">
        <span className="cdash-req-card__val">{selectedLtrs}L</span>
        <span className="cdash-req-card__label">per day</span>
      </div>

      <div className="cdash-req-card__field">
        <label className="cdash-req-card__field-label">Select Daily Quantity</label>
        <select
          className="cdash-req-card__select"
          value={selectedLtrs}
          onChange={e => setSelectedLtrs(parseFloat(e.target.value))}
        >
          {LITRE_OPTIONS.map(l => (
            <option key={l} value={l}>{l} Litre{l !== 1 ? 's' : ''}</option>
          ))}
        </select>
      </div>

      {/* ✅ FIX: no icon, just text, center aligned */}
      <button
        className={`cdash-req-card__btn ${saved ? 'cdash-req-card__btn--saved' : ''}`}
        onClick={handleUpdate}
        disabled={loading}
      >
        {loading ? 'Updating...' : saved ? 'Updated' : 'Update Requirement'}
      </button>
    </div>
  );
};

/* ══════════════════════════════════
   MAIN
══════════════════════════════════ */
const CustomerDashboard = () => {
  const navigate       = useNavigate();
  const [showQR, setShowQR] = useState(false);

  const stats = [
    { label: 'Delivered',  value: '24',      sub: 'This Month', color: '#22c55e',                Icon: CheckCircle   },
    { label: 'Missed',     value: '3',        sub: 'This Month', color: '#f59e0b',                Icon: AlertTriangle },
    { label: 'Total Milk', value: '48L',      sub: 'This Month', color: 'var(--secondary-color)', Icon: Droplets      },
    { label: 'Pending',    value: 'Rs 2,200', sub: 'This Month', color: '#f59e0b',                Icon: CreditCard    },
  ];

  const disputes = [
    { id: '#TKT-101', subject: 'Missed delivery on Feb 25', status: 'Open',     date: 'Feb 25, 2026' },
    { id: '#TKT-098', subject: 'Wrong quantity delivered',   status: 'Resolved', date: 'Feb 18, 2026' },
  ];

  // const summary = [
  //   { label: 'Total Days',   value: '27',       color: 'var(--tertiary-color)' },
  //   { label: 'Delivered',    value: '24 days',  color: '#22c55e'               },
  //   { label: 'Total Litres', value: '48L',       color: 'var(--tertiary-color)' },
  //   { label: 'Total Bill',   value: 'Rs 7,200', color: 'var(--tertiary-color)' },
  //   { label: 'Paid',         value: 'Rs 5,000', color: '#22c55e'               },
  //   { label: 'Pending',      value: 'Rs 2,200', color: '#f59e0b'               },
  //   { label: 'Missed',       value: '3 days',   color: '#ef4444'               },
  //   { label: 'Rate',         value: 'Rs 150/L', color: 'var(--tertiary-color)' },
  // ];

  return (
    <CustomerMasterLayout>
      <div className="cdash-wrapper">

        {/* ── Header ── */}
        <div className="cdash-header">
          <div>
            <h1 className="cdash-title">Dashboard</h1>
            <p className="cdash-subtitle">Welcome to Your MilkyWay Dashboard</p>
          </div>
          <button className="cdash-qr-btn" onClick={() => setShowQR(true)}>
            <QrCode size={16} /> My QR Code
          </button>
        </div>

        {/* ── Stat Cards ── */}
        <Row className="g-3 mb-4">
          {stats.map(({ label, value, sub, color, Icon }, i) => (
            <Col xs={6} lg={3} key={i}>
              <div className="cdash-stat-card cdash-anim" style={{ animationDelay: `${i * 0.07}s` }}>
                <div className="cdash-stat-card__top">
                  <div className="cdash-stat-card__icon" style={{ color }}>
                    <Icon size={18} strokeWidth={2} />
                  </div>
                  <span className="cdash-stat-card__sub">{sub}</span>
                </div>
                <p className="cdash-stat-card__val" style={{ color }}>{value}</p>
                <p className="cdash-stat-card__lbl">{label}</p>
              </div>
            </Col>
          ))}
        </Row>

        {/* ── Today's Entry + Daily Requirements ── */}
        <Row className="g-3 mb-4">
          {/* ✅ FIX: cdash-today-card class add — extra space remove */}
          <Col xs={12} lg={7}>
            <div className="cdash-card cdash-today-card cdash-anim" style={{ animationDelay: '0.28s' }}>
              <h2 className="cdash-card__title">Today's Milk Entry</h2>
              <div className="cdash-today">
                <div className="cdash-today__icon">
                  <CheckCircle size={20} color="#22c55e" strokeWidth={2} />
                </div>
                <div>
                  <p className="cdash-today__val">2L Delivered</p>
                  <p className="cdash-today__time">at 6:15 AM</p>
                </div>
              </div>
            </div>
          </Col>

          <Col xs={12} lg={5}>
            <DailyRequirementsCard />
          </Col>
        </Row>

        {/* ── Disputes Table ── */}
        <div className="cdash-card cdash-anim mb-4" style={{ animationDelay: '0.4s' }}>
          <div className="cdash-card__head">
            <h2 className="cdash-card__title">Recent Disputes</h2>
            <span className="cdash-view-all" onClick={() => navigate('/customer/support')}>View All →</span>
          </div>
          <div className="cdash-table-wrap">
            <table className="cdash-table">
              <thead>
                <tr><th>ID</th><th>Subject</th><th>Status</th><th>Date</th></tr>
              </thead>
              <tbody>
                {disputes.map((d, i) => (
                  <tr key={i}>
                    <td className="cdash-table__id">{d.id}</td>
                    <td>{d.subject}</td>
                    <td>
                      <span className={`cdash-badge cdash-badge--${d.status.toLowerCase()}`}>
                        <span className={`cdash-badge__dot cdash-badge__dot--${d.status.toLowerCase()}`} />
                        {d.status}
                      </span>
                    </td>
                    <td className="cdash-table__date">{d.date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Monthly Summary ── */}
        {/* <div className="cdash-card cdash-anim" style={{ animationDelay: '0.47s' }}>
          <div className="cdash-card__head">
            <h2 className="cdash-card__title">Summary of the Month</h2>
            <select className="cdash-month-select">
              <option>February 2026</option>
              <option>January 2026</option>
              <option>December 2025</option>
            </select>
          </div>
          <div className="cdash-summary">
            {summary.map(({ label, value, color }, i) => (
              <div key={i} className="cdash-summary__item">
                <p className="cdash-summary__label">{label}</p>
                <p className="cdash-summary__value" style={{ color }}>{value}</p>
              </div>
            ))}
          </div>
        </div> */}

      </div>
      {showQR && <QRModal onClose={() => setShowQR(false)} />}
    </CustomerMasterLayout>
  );
};

export default CustomerDashboard;